/**
 * Account API
 *
 * Read-only Dataverse API for Dynamics CRM Accounts.
 * Queries the standard 'accounts' table (not a vba_ custom table).
 *
 * Customer type codes are fetched from the Dataverse metadata API at
 * first use, so the app works across environments without hardcoding.
 */

import {
  dataverseClient,
  type QueryOptions,
} from '@shared/api';
import { escapeODataString } from '@shared/api/dataverse/utils';
import { assertGuid } from '@shared/lib';
import type { DataverseEntity } from '@shared/api/dataverse/types';
import type {
  Account,
  AccountQueryParams,
  CustomerTypeOption,
} from '../model/types';
import { GROUP_TYPE_LABELS } from '../model/types';

// =============================================================================
// Dataverse Entity Shape
// =============================================================================

interface AccountEntity extends DataverseEntity {
  accountid: string;
  name: string;
  accountnumber?: string;
  customertypecode?: number;
  'customertypecode@OData.Community.Display.V1.FormattedValue'?: string;
  emailaddress1?: string;
  telephone1?: string;
  address1_city?: string;
  address1_stateorprovince?: string;
  '_dts_brokerofrecord_value'?: string | null;
  '_dts_brokerofrecord_value@OData.Community.Display.V1.FormattedValue'?: string;
}

// =============================================================================
// Customer Type Metadata Cache
// =============================================================================

let customerTypeOptions: CustomerTypeOption[] | null = null;
let groupTypeCodes: number[] | null = null;
let metadataPromise: Promise<void> | null = null;

interface MetadataResponse {
  OptionSet: {
    Options: Array<{
      Value: number;
      Label: {
        LocalizedLabels: Array<{ Label: string; LanguageCode: number }>;
      };
    }>;
  };
}

/**
 * Fetch the customertypecode option set from Dataverse metadata.
 * Called once, then cached for the session.
 */
async function ensureMetadata(): Promise<void> {
  if (customerTypeOptions) return;
  if (metadataPromise) return metadataPromise;

  metadataPromise = (async () => {
    try {
      const metadataPath =
        "EntityDefinitions(LogicalName='account')/Attributes(LogicalName='customertypecode')" +
        '/Microsoft.Dynamics.CRM.PicklistAttributeMetadata' +
        '?$select=LogicalName&$expand=OptionSet($select=Options)';

      const response = await dataverseClient.fetchRaw<MetadataResponse>(metadataPath);

      const options = response.OptionSet?.Options ?? [];
      customerTypeOptions = options.map((opt) => ({
        value: opt.Value,
        label: opt.Label?.LocalizedLabels?.[0]?.Label ?? `Code ${opt.Value}`,
      }));

      // Resolve group type codes by matching labels
      groupTypeCodes = customerTypeOptions
        .filter((opt) => (GROUP_TYPE_LABELS as readonly string[]).includes(opt.label))
        .map((opt) => opt.value);

      console.log('[AccountAPI] Customer type codes loaded:', customerTypeOptions.length, 'options. Group codes:', groupTypeCodes);
    } catch (err) {
      console.error('[AccountAPI] Failed to fetch customer type metadata:', err);
      // Fallback: no type filtering, reset promise so next call retries
      customerTypeOptions = [];
      groupTypeCodes = [];
      metadataPromise = null;
    }
  })();

  return metadataPromise;
}

// =============================================================================
// Public metadata accessors
// =============================================================================

export async function getCustomerTypeOptions(): Promise<CustomerTypeOption[]> {
  await ensureMetadata();
  return customerTypeOptions ?? [];
}

export async function getGroupTypeCodes(): Promise<number[]> {
  await ensureMetadata();
  return groupTypeCodes ?? [];
}

export function getCachedCustomerTypeLabel(code: number): string | undefined {
  return customerTypeOptions?.find((o) => o.value === code)?.label;
}

// =============================================================================
// Entity Mapping
// =============================================================================

function mapToDomain(entity: AccountEntity): Account {
  return {
    id: entity.accountid,
    name: entity.name,
    accountNumber: entity.accountnumber ?? undefined,
    customerTypeCode: entity.customertypecode ?? undefined,
    customerTypeName:
      (entity['customertypecode@OData.Community.Display.V1.FormattedValue'] as string)
      ?? (entity.customertypecode != null ? getCachedCustomerTypeLabel(entity.customertypecode) : undefined)
      ?? undefined,
    stateCode: entity.statecode ?? undefined,
    email: entity.emailaddress1 ?? undefined,
    phone: entity.telephone1 ?? undefined,
    city: entity.address1_city ?? undefined,
    state: entity.address1_stateorprovince ?? undefined,
    brokerOfRecordContactId: entity['_dts_brokerofrecord_value'] ?? undefined,
    brokerOfRecordName:
      (entity['_dts_brokerofrecord_value@OData.Community.Display.V1.FormattedValue'] as string)
      ?? undefined,
  };
}

// =============================================================================
// Query Building
// =============================================================================

function buildQueryOptions(params: AccountQueryParams, resolvedGroupCodes: number[]): QueryOptions {
  const options: QueryOptions = {
    select: [
      'accountid', 'name', 'accountnumber', 'customertypecode',
      'emailaddress1', 'telephone1',
      'address1_city', 'address1_stateorprovince',
      '_dts_brokerofrecord_value',
    ],
    orderBy: 'name asc',
    top: params.top ?? 50,
  };

  const filters: string[] = [];

  if (params.nameFilter) {
    filters.push(`contains(name, '${escapeODataString(params.nameFilter)}')`);
  }

  const typeCodes = params.customerTypeCodes ?? resolvedGroupCodes;
  if (typeCodes.length === 1) {
    filters.push(`customertypecode eq ${typeCodes[0]}`);
  } else if (typeCodes.length > 1) {
    const typeFilter = typeCodes.map((c) => `customertypecode eq ${c}`).join(' or ');
    filters.push(`(${typeFilter})`);
  }

  if (params.activeOnly !== false) {
    filters.push('statecode eq 0');
  }

  if (filters.length > 0) {
    options.filter = filters.join(' and ');
  }

  return options;
}

// =============================================================================
// API Functions
// =============================================================================

async function listAccounts(params: AccountQueryParams = {}): Promise<Account[]> {
  const codes = await getGroupTypeCodes();
  const options = buildQueryOptions(params, codes);
  const response = await dataverseClient.list<AccountEntity>('accounts', options);
  return response.value.map(mapToDomain);
}

async function getAccount(id: string): Promise<Account> {
  const options: QueryOptions = {
    select: [
      'accountid', 'name', 'accountnumber', 'customertypecode',
      'emailaddress1', 'telephone1',
      'address1_city', 'address1_stateorprovince',
      '_dts_brokerofrecord_value',
    ],
  };
  const entity = await dataverseClient.get<AccountEntity>('accounts', id, options);
  return mapToDomain(entity);
}

async function searchGroups(query: string, top = 20): Promise<Account[]> {
  return listAccounts({ nameFilter: query, top });
}

/**
 * Fetch accounts by id with no customertypecode filter. Batched to keep
 * the OData URL length manageable. Use when you have a precomputed set
 * of account ids (e.g. accounts referenced by opportunities) and want
 * every one back regardless of how it's categorized in CRM.
 */
async function listByIds(ids: string[]): Promise<Account[]> {
  if (ids.length === 0) return [];

  const BATCH = 50;
  const results: Account[] = [];

  for (let i = 0; i < ids.length; i += BATCH) {
    const batch = ids.slice(i, i + BATCH);
    const filter = batch.map((id) => `accountid eq ${assertGuid(id, 'accountId')}`).join(' or ');
    const options: QueryOptions = {
      select: [
        'accountid', 'name', 'accountnumber', 'customertypecode',
        'emailaddress1', 'telephone1',
        'address1_city', 'address1_stateorprovince',
        '_dts_brokerofrecord_value',
      ],
      filter,
      top: BATCH,
    };
    const response = await dataverseClient.list<AccountEntity>('accounts', options);
    for (const e of response.value) results.push(mapToDomain(e));
  }

  return results;
}

// =============================================================================
// Export
// =============================================================================

export const accountApi = {
  list: listAccounts,
  get: getAccount,
  listByIds,
  searchGroups,
  getCustomerTypeOptions,
  getGroupTypeCodes,
};
